import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, Globe2, ShoppingCart, Bot, Search, Megaphone, Ship, CheckCircle, Send
} from 'lucide-react';
import Seo from '../../components/Seo';
import ServiceCTA from '../../components/ServiceCTA';

const services = [
  { icon: <Sparkles className="w-6 h-6" />, title: '国際ブランディング', en: 'International Branding', description: '海外市場でも伝わるブランドの世界観・メッセージづくりをサポートします。' },
  { icon: <Globe2 className="w-6 h-6" />, title: '英語サイト制作', en: 'English Website Creation', description: '海外のお客様に向けた、読みやすく信頼感のある英語Webサイトを制作します。' },
  { icon: <ShoppingCart className="w-6 h-6" />, title: '海外向けEC', en: 'Global E-commerce', description: '海外向けのオンライン販売の仕組みづくりと運用をお手伝いします。' },
  { icon: <Bot className="w-6 h-6" />, title: 'AI商品マーケティング', en: 'AI Product Marketing', description: 'AIを活用し、商品の魅力を効率よく多言語で発信します。' },
  { icon: <Search className="w-6 h-6" />, title: '海外SEO', en: 'International SEO', description: '海外の検索エンジンで見つけてもらえるよう、多言語SEOを最適化します。' },
  { icon: <Megaphone className="w-6 h-6" />, title: 'Google・Meta広告', en: 'Google & Meta Advertising', description: '海外顧客に届くGoogle・Meta広告の設計と運用を行います。' },
  { icon: <Ship className="w-6 h-6" />, title: '輸出マーケティング戦略', en: 'Export Marketing Strategy', description: 'ターゲット市場の選定から販路づくりまで、輸出戦略を一緒に描きます。' },
];

const GlobalExpansion = () => {
  return (
    <div>
      <Seo
        title="海外進出支援 Global Expansion | Sakura Local Business Support"
        description="日本企業の海外進出を、国際ブランディング・英語サイト制作・海外EC・AI商品マーケティング・海外SEO・Google/Meta広告・輸出戦略で総合的に支援します。"
        keywords="海外進出支援,Global Expansion,Export Support,英語サイト制作,海外EC,International SEO,AI Marketing,International Marketing,SLBS"
      />

      {/* Hero */}
      <section className="py-16 md:py-20" style={{ backgroundColor: '#FFF7FA' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center fade-in">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase mb-5 px-4 py-1.5 rounded-full bg-white" style={{ color: '#C77A93' }}>
              Global Expansion
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#2C2C2C' }}>
              海外進出支援
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              日本企業が自信を持って海外のお客様へ届くように。
              バイリンガル対応とAIを活用し、グローバル展開を一歩ずつサポートします。
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
              サービス内容
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              目標やターゲット市場に合わせて、必要なサポートを組み合わせてご提案します。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {services.map((item, index) => (
              <div key={index} className="bg-white p-7 rounded-2xl border border-gray-100 shadow-sm hover-lift" data-testid={`global-expansion-card-${index}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#FFF7FA', color: '#C77A93' }}>
                  {item.icon}
                </div>
                <p className="text-xs font-medium mb-1" style={{ color: '#C77A93' }}>{item.en}</p>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#2C2C2C' }}>{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="py-16" style={{ backgroundColor: '#FFF7FA' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#2C2C2C' }}>こんな企業様に向いています</h2>
            <ul className="space-y-3">
              {[
                '海外で商品・サービスを販売したい',
                '英語のWebサイトを新しくつくりたい',
                '海外向けの広告を始めたい',
                '海外のお客様や取引先を見つけたい',
                'グローバルなブランディングを強化したい',
              ].map((item, i) => (
                <li key={i} className="flex items-start">
                  <CheckCircle className="w-5 h-5 mt-1 mr-3 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/global-business" className="inline-block text-sm text-[#C77A93] hover:text-[#2C2C2C] font-medium mt-6">
              Global Business Solutions の全体像を見る →
            </Link>
          </div>
        </div>
      </section>

      <ServiceCTA
        title="海外進出について、お気軽にご相談ください。"
        description="どの国・地域を目指すべきか分からない段階でも、まずはお話をお伺いします。"
        buttonText="無料相談はこちら"
      />
    </div>
  );
};

export default GlobalExpansion;
